import React from 'react';
import './index.sass'
import ProjectContentContainer from "../../../components/ProjectContentContainer";
import {Grid, useMediaQuery, useTheme} from "@mui/material";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WordCard from "../../../components/WordCard";
import {MoveToTopOnMount} from "../../../utils/pageScroll";
import Head from "../../../components/Head";
import engagementMap from "../../../assets/images/engagementMap.png";
import ProjectRecord from "../../../components/ProjectRecord";
import coverImg from "./stand-up-cover.png";
import waterReminder from "./water-reminder.png";
import builtInReminder from "./builtin.png";
import builtInReminderApp from "./builtin-app.png";
import reviews from "./reviews.png";
import users from "./users.png";
import Quote from "../../../components/Quote";
import NumberInCircle from "../../../components/NumberInCircle";
import goHam from "../go_ham/go_ham_cover_bg.png";
import bookCover1 from "../book_cover/book_cover1_thumb.png";

const GridContainerEngagementMap = ({children, sx}) => (
    <Grid container spacing={'2rem'} sx={sx}>
        {children}
    </Grid>
)

const QuoteSUR = ({children}) => (
    <Quote color={'#FF583B'}>
        {children}
    </Quote>
)

const NumberInCircleSUR = ({children}) => (
    <NumberInCircle bgColor={'#FF583B'}>
        {children}
    </NumberInCircle>
)

const WhiteCard = ({children, className}) => (
    <WordCard bgColor={'white'} className={className}>
        {children}
    </WordCard>
)

const OrangeCard = ({children, className}) => (
    <WordCard bgColor={'#FF583B'} className={className}>
        {children}
    </WordCard>
)

const FutureWorkCard = ({children}) => (
    <WordCard bgColor={'#FFFBF0'}>
        {children}
    </WordCard>
)

function Index({cursor}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <div>
            <Head
                title={'Alexa Stand Up Reminder'}
                desc={'Exercising app re-establishing the Columbia community during COVID-19 through human interaction and gamification.'}
            />
            <MoveToTopOnMount />

            <div className={`sur__cover ${isMobile && 'sur__cover_mobile'}`}>
                <img src={coverImg} alt={'Logo of stand up reminder with alexa devices beside it'} />
            </div>

            <ProjectContentContainer>
                <h1 className={'sur__title'}>
                    <span className="sur__title__regular">
                        The&nbsp;
                    </span>
                    Alexa Stand Up Reminder&nbsp;
                    <span className="sur__title__regular">
                        is an productivity Alexa skill that reminds you to stand up every 45 minutes and take a break for 5 minutes. During the break, it suggests  fun and relaxing activities!
                    </span>
                </h1>

                {/*Intro*/}
                <GridContainerEngagementMap sx={{marginTop: '2rem', marginBottom: '4rem'}}>
                    <Grid item xs={12} md={6}>
                        <h2>
                            Sitting for a long time has some serious health concerns and we wanted to help you in a natural way
                        </h2>
                        <p>
                            Available in the skill store (
                            <a
                                href={'https://www.amazon.com/Default-User-Stand-Up-Reminder/dp/B09NNWRHGX/ref=sr_1_1?keywords=stand+up+reminder&qid=1639709191&s=digital-skills&sr=1-1'}
                                className={'sur__link'}
                            >
                                link
                            </a>
                            ) for free with a&nbsp;
                            <span className="sur__intro__star">
                                4.8 star&nbsp;
                            </span>
                            review!
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <Grid container>
                            <Grid item xs={5} md={12}>
                                <h2 className={'sur__intro__h2'}>My Role</h2>
                                <p>
                                    Ideation, Research, Design, Implementation (JS), User Testing
                                </p>
                            </Grid>

                            <Grid item xs={1} sx={{ display: { md: 'none', xs: 'block' } }} />

                            <Grid item xs={5} md={12}>
                                <h2 className={'sur__intro__h2'}>Results</h2>
                                <p>
                                    More than 400 unique users and 700 sessions.
                                </p>
                                <p>
                                    Improved efficiency in working and made users actually take a break. Eased mental and back pain.
                                </p>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Research*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Research</h1>
                        <p>
                            We explored 30 pre-existing Alexa skills and asked the following questions:
                        </p>
                        <ol>
                            <li>
                                When and why would you use this skill?
                            </li>
                            <li>
                                What did you expect it to happen?
                            </li>
                            <li>
                                How was the experience?
                            </li>
                            <li>
                                How could it be improved?
                            </li>
                            <li>
                                What is the alternative to doing this with Alexa? How does it compare?
                            </li>
                            <li>
                                Would you use it again?
                            </li>
                        </ol>
                        <p>
                            From answering these questions, we gained an insight that Alexa tends to be good at performing simple tasks that assist the users on the side.
                        </p>
                        <p>
                            This is where the idea of a stand up reminder came into mind because personally, I had recently bought a standing desk to reduce my back pain from sitting for a long time in front of a computer. However, I realized that I would often get too consumed into the work and forget to utilize the desk.
                        </p>
                        <p>
                            In relation to this idea, we explored similar pre-existing apps and solutions.
                        </p>
                    </Grid>

                    {/*Water reminder*/}
                    <Grid item xs={12}>
                        <h2>Water Reminder</h2>
                        <Grid container>
                            <Grid item xs={12} md={6}>
                                <p>
                                    The Water Reminder (
                                    <a
                                        href={'https://www.amazon.com/Skill-Genius-Water-Reminder/dp/B081ZV3VZP'}
                                        className="sur__link"
                                    >
                                        link
                                    </a>
                                    ) reminds you to drink water for 8 hours after the initial launch.
                                </p>
                                <p>
                                    The skill is simple and works well to fulfill the user goal (except users claim that the volume is too loud).
                                </p>
                                <p>
                                    In the stand up reminder, not only do we want to remind the user to stand up, but also keep them stood up for some time so they can get some rest.
                                </p>
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={5} className={'sur__grid_center'}>
                                <img
                                    src={waterReminder}
                                    alt={'logo of water reminder'}
                                    className={'sur__img'}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/*built in*/}
                    <Grid item xs={12}>
                        <h2>Alexa Built-in Reminder System</h2>

                        <img
                            src={builtInReminder}
                            className={'sur__img'}
                            alt={'banner page of the built in reminder system'}
                        />

                        <Grid container>
                            <Grid item xs={12} md={6}>
                                <p>
                                    Alexa also has a builtin reminder feature that can be set recurring.
                                </p>
                                <p>
                                    However, it is really hard to use as a college student / worker when your schedule is not the same each day and you need to go into the settings every day to adjust the reminders.
                                </p>
                                <p>
                                    It was also pretty annoying to navigate to this page and change each of the times because there were multiple pages and multiple actions needed.
                                </p>
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={5} className={'sur__grid_center'}>
                                <img
                                    src={builtInReminderApp}
                                    alt={'the built in reminder system on the alexa mobile app'}
                                    className={'sur__img'}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*The users*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>The User</h1>

                        <Grid container>
                            <Grid item xs={12} md={6}>
                                <p>
                                    A prototypical user is a college student studying at home and needing to use their laptops for a long time to complete their homework.
                                </p>
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={5} className={'sur__grid_center'}>
                                <img
                                    src={users}
                                    alt={'users'}
                                    className={'sur__img'}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/*why / when*/}
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12} md={5}>
                                <h2>Why</h2>
                                <WhiteCard className={'sur__white_card_link sur__white_card_h_auto sur__white_card_mb_1'}>
                                    <div>
                                        Sitting too long is harmful to health (ref:&nbsp;
                                        <a
                                            href={'https://www.betterhealth.vic.gov.au/health/healthyliving/the-dangers-of-sitting'}
                                            className={'sur__link'}
                                        >
                                            https://www.betterhealth.vic.gov.au/health/healthyliving/the-dangers-of-sitting
                                        </a>
                                        ), but it is easy to forget for students to stand up and rest when they are too consumed with their homework.
                                    </div>
                                </WhiteCard>
                                <WhiteCard className={'sur__white_card_h_auto'}>
                                    To refresh their minds so they can work more efficiently for a longer time.
                                </WhiteCard>
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={5}>
                                <h2>When</h2>
                                <WhiteCard className={'sur__white_card_h_auto'}>
                                    Everyday when they are studying for a long time (several hours) at home.
                                </WhiteCard>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/*trigger*/}
                    <Grid item xs={12}>
                        <h2>Trigger</h2>
                        <Grid container alignItems={'center'}>
                            <Grid item xs={12} md={3}>
                                <WhiteCard>
                                    Doing: They’re getting ready to do homework.
                                </WhiteCard>
                            </Grid>

                            <Grid item xs={12} md={1}>
                                <Grid container justifyContent={'center'}>
                                    {isMobile ? (
                                      <ExpandMoreIcon className={'sur__arrow'} />
                                    ): (
                                      <ChevronRightIcon className={'sur__arrow'} />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <WhiteCard>
                                    Feeling: Troublesome.
                                </WhiteCard>
                            </Grid>

                            <Grid item xs={12} md={1}>
                                <Grid container justifyContent={'center'}>
                                    {isMobile ? (
                                      <ExpandMoreIcon className={'sur__arrow'} />
                                    ): (
                                      <ChevronRightIcon className={'sur__arrow'} />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <WhiteCard>
                                    Thinking / Saying: “This is gonna be a long battle but I should take breaks in the middle to refresh my mind”.
                                </WhiteCard>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/*satisfaction*/}
                    <Grid item xs={12}>
                        <h2>Satisfaction</h2>
                        <Grid container alignItems={'center'}>
                            <Grid item xs={12} md={4}>
                                <WhiteCard>
                                    Thinking: “Oh it took me a long time to finish the homework! But I’m glad that I took some rest in between. My eyes didn’t feel that strained this time.”
                                </WhiteCard>
                            </Grid>

                            <Grid item xs={12} md={1}>
                                <Grid container justifyContent={'center'}>
                                    {isMobile ? (
                                      <ExpandMoreIcon className={'sur__arrow'} />
                                    ): (
                                      <ChevronRightIcon className={'sur__arrow'} />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <WhiteCard>
                                    Feeling: Accomplished then relieved.
                                </WhiteCard>
                            </Grid>

                            <Grid item xs={12} md={1}>
                                <Grid container justifyContent={'center'}>
                                    {isMobile ? (
                                      <ExpandMoreIcon className={'sur__arrow'} />
                                    ): (
                                      <ChevronRightIcon className={'sur__arrow'} />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <WhiteCard>
                                    Doing next: Submit their homework and go have fun.
                                </WhiteCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Design goals*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>The Design Goals</h1>

                        <Grid container alignItems={'center'}>
                            <Grid item xs={12} md={4}>
                                <OrangeCard className={`sur__orange_card ${ isMobile && 'sur__white_card_mb_1'}`}>
                                    Short and to the point. ("Stand up and take a break. Move your eyes side to side and go get some water. We’ll get back to you in 5 minutes")
                                </OrangeCard>
                            </Grid>

                            <Grid item xs={1} />

                            <Grid item xs={12} md={3}>
                                <OrangeCard className={`sur__orange_card ${ isMobile && 'sur__white_card_mb_1'}`}>
                                    Notify users when to work and when to rest through voice.
                                </OrangeCard>
                            </Grid>

                            <Grid item xs={1} />

                            <Grid item xs={12} md={3}>
                                <OrangeCard className={`sur__orange_card ${ isMobile && 'sur__white_card_mb_1'}`}>
                                    Randomize the exercise while resting so it adds some fun.
                                </OrangeCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*product*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>The Product</h1>

                        <Grid container>
                            <Grid item xs={12}>
                                <iframe width="100%" height="415" src="https://www.youtube.com/embed/b4XaCEBrp5Y"
                                        title="YouTube video player" frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen />
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*user testing*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>User Testing</h1>

                        <Grid container>
                            <Grid item xs={12}>
                                <h2>Why</h2>
                            </Grid>

                            <Grid item xs={12}>
                                <p>
                                    They were feeling pretty stressed thinking about how much work they had to complete before the deadlines that were coming up next week.
                                </p>
                                <p>
                                    They know that they should take some rest in between because they get more stressed and tired when they continuously work in one sitting. However, they always lose track of time and cannot stop until they are reminded such as to cook lunch by their girlfriend
                                </p>
                                <p>
                                    Their back was already hurting from sitting too long recently so they wanted to take stretches in between working since the pain disturbed them from working efficiently
                                </p>
                            </Grid>
                        </Grid>

                        <Grid container>
                            <Grid item xs={12}>
                                <h2>When</h2>
                            </Grid>

                            <Grid item xs={12}>
                                <p>
                                    Used it every morning from 10am - 12pm and night 9pm - 12am to work on final projects that they needed to submit soon
                                </p>
                                <p>
                                    As they walked to their desk to start working, they invoked the skill hoping to have a more organized working session with breaks
                                </p>
                            </Grid>
                        </Grid>

                        <Grid container>
                            <Grid item xs={12}>
                                <h2>Satisfaction</h2>
                            </Grid>

                            <Grid item xs={12}>
                                <p>
                                    They were very satisfied with the skill. They took short breaks during studying, which led to do my work more efficiently during the study sessions.
                                </p>
                                <p>
                                    Also felt less fatigue mentally. During the short breaks, taking a step back from the problem they were working on made time to reflect and think of a solution to it.
                                </p>
                            </Grid>

                            <Grid item xs={12}>
                                <h3>4.8 Star Review on the Skill Store!</h3>

                                <Grid container>
                                    <Grid item xs={12} md={6}>
                                        <img
                                            src={reviews}
                                            alt={'screenshot of reviews on the alexa skill store'}
                                            className={'sur__img'}
                                        />
                                    </Grid>

                                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                                    <Grid item xs={12} md={5}>
                                        <QuoteSUR>
                                            E-mail Review: “I was looking for a skill that would remind me to take a break and breathe/meditate every hour or so. None of them work!! Except for your wonderful competent app!!!”
                                        </QuoteSUR>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid container>
                            <Grid item xs={12}>
                                <h2>Statistics</h2>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container spacing={'1rem'}>
                                    <Grid item xs={12} md={4} className="sur__stats">
                                        <NumberInCircleSUR>400</NumberInCircleSUR>
                                        <div>unique users</div>
                                    </Grid>

                                    <Grid item xs={12} md={4} className="sur__stats">
                                        <NumberInCircleSUR>700</NumberInCircleSUR>
                                        <div>skill sessions</div>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Evaluation*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Evaluation</h1>

                        <Grid container alignItems={'center'}>
                            <Grid item xs={12}>
                                <h2>What Went Well</h2>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <WhiteCard className={`${isMobile && 'sur__white_card_mb_1'}`}>
                                    Actually took rests based on Alexa’s suggestions between doing homework, which usually doesn’t really happen until finished.
                                </WhiteCard>
                            </Grid>

                            <Grid item xs={1} />

                            <Grid item xs={12} md={3}>
                                <WhiteCard className={`${isMobile && 'sur__white_card_mb_1'}`}>
                                    Was able to fully concentrate during the 45min work sessions, and got more work done than usual in the same time span
                                </WhiteCard>
                            </Grid>

                            <Grid item xs={1} />

                            <Grid item xs={12} md={3}>
                                <WhiteCard className={`${isMobile && 'sur__white_card_mb_1'}`}>
                                    Less stress mentally and pain in the back since you don’t have to concentrate the whole time
                                </WhiteCard>
                            </Grid>
                        </Grid>

                        <Grid container>
                            <Grid item xs={12}>
                                <h2>Limitations</h2>
                            </Grid>

                            <Grid item xs={12}>
                                <p>
                                    Since we utilized the Alexa Reminders API, there were many constraints in the final solution that we had to follow. One of the major negative feedback we received from users is that there are too many back and forth between the user and Alexa before starting the working sessions. The reason for this was because the Reminders API forces us to confirm every time there is an user input. Hopefully in the future, this problem will be revisited.
                                </p>
                            </Grid>
                        </Grid>

                        <Grid container spacing={`${ isMobile && '2rem'}`}>
                            <Grid item xs={12}>
                                <h2>Future Work</h2>
                            </Grid>

                            <Grid item xs={12} md={5}>
                                <FutureWorkCard className={`${isMobile && 'sur__white_card_mb_1'}`}>
                                    Have a way to inform the user of remaining time in the session since users sometimes wondered how much time had passed but had no way of knowing.
                                </FutureWorkCard>
                            </Grid>

                            <Grid item xs={1} />

                            <Grid item xs={12} md={5}>
                                <FutureWorkCard className={`${isMobile && 'sur__white_card_mb_1'}`}>
                                    A command that could cancel the study and resting sessions easily.
                                </FutureWorkCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Other work*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Other Work</h1>

                        <Grid container spacing={'2rem'}>
                            <Grid item xs={12}>
                                <ProjectRecord
                                    to={'/work/GO-HAM'}
                                    title={'GO HAM!'}
                                    subtitle={'Re-establishing the Columbia community during COVID-19 through exercise'}
                                    desc={'Exercising app with emphasis on spontaneous human interaction through gamification. ' +
                                    'Helps motivate and keep students in a routine to exercise in a limited space with friends.'}
                                    img={goHam}
                                    imgAlt={'smart phone app design of go ham'}
                                    bgColor={'linear-gradient(127.67deg, rgba(17, 141, 255, 0.2) 60.94%, rgba(217, 242, 249, 0.2) 76.79%, rgba(255, 255, 255, 0.0252632) 92.97%), #D5FFCB'}
                                    cursor={cursor}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ProjectRecord
                                    to={'/work/EngagementMap'}
                                    title={'EngagementMap'}
                                    subtitle={'A real-time, location-based, collective, and anonymous feedback system in the classroom'}
                                    desc={'Provides instructors with a glanceable spatial visualization of the classroom to help them understand ' +
                                    'how students are feeling.'}
                                    img={engagementMap}
                                    imgAlt={'smart phone app design of engagement map'}
                                    bgColor={'linear-gradient(127.67deg, rgba(253, 131, 255, 0.2) 60.94%, rgba(255, 139, 139, 0) 76.79%, rgba(255, 255, 255, 0.0252632) 92.97%), #ECF5FF'}
                                    cursor={cursor}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ProjectRecord
                                    to={'/work/book-covers'}
                                    title={'Published Book Covers'}
                                    subtitle={'I designed 2 book covers that were published in Japan'}
                                    img={bookCover1}
                                    imgAlt={'logo of the book cover i designed about inclusive education'}
                                    bgColor={'linear-gradient(127.67deg, rgba(17, 141, 255, 0.3) 60.94%, rgba(217, 242, 249, 0.3) 76.79%, rgba(255, 255, 255, 0.0378947) 92.97%), #F3FFF0'}
                                    cursor={cursor}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

            </ProjectContentContainer>

        </div>
    );
}

export default Index;